import React, { useCallback, useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import PlatesModal from './PlatesModal';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { licensePlateRecognitionService, PlateData } from '../../../services/central-api/license-plate-recognition';
import { ClientError } from '../../../services/central-api/base-service';
import MyDataTable from '../../../components/MyDataTable';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { LPRParamList } from '../../../typings/Params';
import CustomButton from '../../../components/CustomButton';
import FilterInput from '../../../components/Filter/components/FilterInput';
import LprPlateFilters from './Filters';
import { useAuth } from '../../../context/Auth';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';

export default function Plates({ navigation }: { navigation: Navigation; }) {

    const route = useRoute<RouteProp<LPRParamList, 'Plates'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useState<LPRParamList['Plates']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [totalItems, setTotalItems] = useState(0);

    const [plateText, setPlateText] = useState(filters.plate ?? '');
    const [plates, setPlates] = useState<PlateData[]>([]);
    const [selectedPlate, setSelectedPlate] = useState<PlateData>();
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['plates', filters],
        queryFn: () => licensePlateRecognitionService.getPlates(filters),
        enabled: !!authenticatedUser,
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        setPlates(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();
        }
    }, [isFocused, filters]));

    function handleFilters() {
        setFilters({
            ...filters,
            plate: plateText !== '' ? plateText : undefined,
            page: 0
        });

        navigation.setParams({
            plate: plateText !== '' ? plateText : undefined,
            page: 1
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            handleFilters();
        }
    }

    return <Container>
        <Header
            title={translate('plates')}
            actions={<>{
                authenticatedUser
                && (authenticatedUser.permissions.lpr_module_access
                    || authenticatedUser.permissions.lpr_edit_denylist
                    || authenticatedUser.permissions.lpr_edit_illegal_debris
                ) && <CustomButton
                    onPress={() => {
                        setSelectedPlate(undefined);
                        setModalVisible(true);
                    }}
                    text={translate('register')}
                />
            }</>}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredPlates')}
                zIndex={2}
                filterInput={<FilterInput
                    value={plateText}
                    onChange={setPlateText}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
                filter={<LprPlateFilters
                    filters={filters}
                    setFilters={setFilters}
                    plateFilter={plateText}
                    setPlateFilter={setPlateText}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('plates'),
                        selector: row => row.plate,
                        grow: 0
                    },
                    {
                        name: translate('type'),
                        selector: row => translate(row.type),
                        grow: 1,
                        wrap: true
                    },
                    {
                        name: translate('description'),
                        selector: row => row.description,
                        grow: 1,
                        wrap: true
                    },
                    {
                        button: true,
                        cell: row => {
                            if (authenticatedUser?.permissions.lpr_module_access
                                || authenticatedUser?.permissions.lpr_edit_denylist
                                || authenticatedUser?.permissions.lpr_edit_illegal_debris) {
                                const actions: Action[] = [{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedPlate(row);
                                        setModalVisible(true);
                                    }
                                }, {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedPlate(row);
                                        setDeleteModalVisible(true);
                                    },
                                }];
                                return <DropdownButton actions={actions} />;
                            }
                            return <></>;
                        }
                    }]}
                    data={plates}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    totalItems={totalItems}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        {selectedPlate &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedPlate.plate}
                onSubmit={async () => {
                    try {
                        await licensePlateRecognitionService.deletePlate(selectedPlate.id);
                        Toast.show({
                            type: 'sentinelxSuccess',
                            text1: translate('ActionSuccessfully'),
                        });

                        refetch();
                    } catch (err) {
                        if (err instanceof ClientError) {
                            return Toast.show({
                                type: 'sentinelxError',
                                text1: translate(err.message),
                            });
                        }

                        console.error(err);
                        Toast.show({
                            type: 'sentinelxError',
                            text1: translate('unexpectedError'),
                        });
                    }
                }}
            />
        }

        {/* create/edit plate modal */}
        <PlatesModal
            onClose={() => {
                setSelectedPlate(undefined);
                setModalVisible(false);
                refetch();
            }}
            isModalVisible={isModalVisible}
            plateId={selectedPlate?.id}
        />

    </Container>;
}
